import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, FileText, Sparkles, CheckCircle2 } from "lucide-react";
import { motion } from "motion/react";
import { HeroBackground } from "./Animations";
import { HeroSearchBar } from "./SearchBar";
import { CategoryCards } from "./CategoryCards";
import { CalculatorCard } from "./CalculatorCard";

interface HeroProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onExploreClick?: () => void;
}

export const HeroSection: React.FC<HeroProps> = ({
  searchQuery,
  setSearchQuery,
  onExploreClick,
}) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>("investment");

  const handleSelectCategory = (catId: string) => {
    setSelectedCategory(catId);
  };

  return (
    <section className="relative w-full overflow-hidden pt-6 pb-12 sm:pt-10 sm:pb-16 px-4 sm:px-6 lg:px-8 bg-[#050816]">
      {/* Background Animated Gradient, Grid & Particle Auras */}
      <HeroBackground />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* ================= LEFT SIDE (55% on Desktop: lg:col-span-7) ================= */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="lg:col-span-7 space-y-6 sm:space-y-7 text-left"
          >
            {/* Small Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 text-xs sm:text-sm text-amber-400 font-semibold tracking-tight shadow-md shadow-amber-500/5 backdrop-blur-md"
            >
              <span className="text-sm">🔥</span>
              <span>Free Personal Finance Tools</span>
            </motion.div>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight text-white leading-[1.12]">
              <span>Financial Calculators</span>
              <br />
              <span className="inline-block mt-1">
                <span>for </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 font-black drop-shadow-sm">
                  Smarter Money Decisions
                </span>
              </span>
            </h1>

            {/* Description */}
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl font-light">
              Plan loans, calculate mortgage payments, grow investments, estimate retirement savings and manage debt using powerful financial calculators.
            </p>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 pt-1">
              <button
                onClick={() => {
                  if (onExploreClick) {
                    onExploreClick();
                  } else {
                    document.getElementById("calculator-directory-title")?.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="px-7 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold rounded-2xl text-base transition-all duration-300 shadow-xl shadow-amber-500/15 hover:shadow-amber-500/30 hover:-translate-y-0.5 cursor-pointer flex items-center justify-center gap-2.5"
              >
                <Calculator className="w-5 h-5 text-slate-950" />
                <span>Explore Calculators</span>
              </button>

              <button
                onClick={() => {
                  navigate("/blog");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="px-7 py-4 bg-white/5 border border-white/10 hover:border-amber-500/40 text-slate-200 hover:text-white font-semibold rounded-2xl text-base transition-all hover:bg-white/10 hover:-translate-y-0.5 cursor-pointer flex items-center justify-center gap-2.5 backdrop-blur-md"
              >
                <FileText className="w-5 h-5 text-amber-400" />
                <span>Financial Guides</span>
              </button>
            </div>

            {/* Large Search Bar */}
            <div className="pt-2">
              <HeroSearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onSelectToolCategory={handleSelectCategory}
              />
            </div>

            {/* Category Cards */}
            <div className="pt-2">
              <CategoryCards
                selectedCategory={selectedCategory}
                onSelectCategory={handleSelectCategory}
              />
            </div>

            {/* Trust highlights */}
            <div className="pt-2 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-slate-400 font-medium">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>100% Free Access</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>No Registration</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Client-Side Privacy</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Updated Formulas</span>
              </div>
            </div>
          </motion.div>

          {/* ================= RIGHT SIDE (45% on Desktop: lg:col-span-5) ================= */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            className="lg:col-span-5 w-full sticky top-6"
          >
            <CalculatorCard
              selectedCategory={selectedCategory}
              onSelectCategory={handleSelectCategory}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
